import { cn } from '@goaltree/ui/lib/utils'
import { CheckIcon, LockIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ACCENT_OPTIONS, type Accent, dayAccent, useAccentTheme } from '@/hooks/use-accent-theme'
import { useIsPro } from '@/hooks/use-is-pro'
import { useCategoryFill } from '@/lib/category-color'

const WEEK = [1, 2, 3, 4, 5, 6, 0]
// Weekday order (Mon → Sun) so the row reads like the daily rotation.
const WEEK_OPTIONS = WEEK.map((d) => ACCENT_OPTIONS.find((o) => o.id === dayAccent(d))!)

type Hover = Accent | null

// The accent picker everywhere: seven colours plus a "rotate daily" switch.
export function useAccentPicker(onLocked: () => void) {
  const { t, i18n } = useTranslation()
  const { accent, rotate, setAccent, setRotate } = useAccentTheme()
  const { isPro } = useIsPro()
  const [hover, setHover] = useState<Hover>(null)
  const today = dayAccent(new Date().getDay())

  const lock = (pro: boolean) => (pro && !isPro ? ` · ${t('settings.accentProOnly')}` : '')
  let line: string
  if (hover) {
    const i = WEEK_OPTIONS.findIndex((o) => o.id === hover)
    const day = new Intl.DateTimeFormat(i18n.language, { weekday: 'long' }).format(
      new Date(2024, 0, WEEK[i] || 7),
    )
    line = rotate
      ? `${day}: ${t(`settings.accentName.${hover}`)} · ${t(`settings.accentWhy.${hover}`)}`
      : t(`settings.accentName.${hover}`) + lock(WEEK_OPTIONS[i].pro)
  } else
    line = rotate
      ? t('settings.accentToday', {
          name: t(`settings.accentName.${today}`),
          why: t(`settings.accentWhy.${today}`),
        })
      : t(`settings.accentName.${accent}`)

  function pick(id: Accent) {
    if (WEEK_OPTIONS.find((o) => o.id === id)!.pro && !isPro) return onLocked()
    setRotate(false)
    setAccent(id)
  }

  function toggleRotate(next: boolean) {
    if (next && !isPro) return onLocked()
    setRotate(next)
  }

  return {
    line,
    swatches: { accent, rotate, today, isPro, setHover, pick },
    rotateSwitch: { checked: rotate, locked: !isPro, onChange: toggleRotate },
  }
}

export function AccentSwatches({
  accent,
  rotate,
  today,
  isPro,
  setHover,
  pick,
  size = 28,
  stretch,
  className,
}: ReturnType<typeof useAccentPicker>['swatches'] & {
  size?: number
  stretch?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  const fill = useCategoryFill()
  const icon = Math.round(size * 0.5)
  const hoverProps = (id: Hover) => ({
    onMouseEnter: () => setHover(id),
    onFocus: () => setHover(id),
    onBlur: () => setHover(null),
  })
  const base =
    'relative flex shrink-0 cursor-pointer items-center justify-center rounded-full transition-all hover:scale-110 active:scale-95'

  return (
    <div
      className={cn('flex flex-wrap items-center gap-2', className)}
      onMouseLeave={() => setHover(null)}
    >
      {WEEK_OPTIONS.map((opt) => {
        const active = !rotate && accent === opt.id
        const locked = opt.pro && !isPro
        return (
          <button
            key={opt.id}
            type="button"
            aria-label={t(`settings.accentName.${opt.id}`)}
            aria-pressed={active}
            onClick={() => pick(opt.id)}
            {...hoverProps(opt.id)}
            className={cn(
              base,
              active && 'scale-110',
              rotate &&
                opt.id === today &&
                'ring-2 ring-foreground ring-offset-2 ring-offset-background',
            )}
            style={{
              width: stretch ? undefined : size,
              height: size,
              flex: stretch ? 1 : undefined,
              backgroundColor: fill(opt.color),
            }}
          >
            {active && (
              <CheckIcon
                className="text-white drop-shadow-sm dark:text-[#0a0a0a]"
                style={{ width: icon, height: icon }}
                strokeWidth={3}
              />
            )}
            {locked && !active && (
              <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/30">
                <LockIcon className="size-3 text-white" strokeWidth={2.5} />
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

export function AccentRotateSwitch({
  checked,
  locked,
  onChange,
}: ReturnType<typeof useAccentPicker>['rotateSwitch']) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
    >
      <span className="flex items-center gap-1.5">
        {t('settings.accentRotate')}
        {locked && <LockIcon className="h-3 w-3" strokeWidth={2.5} />}
      </span>
      <span
        className={cn(
          'relative h-5 w-9 shrink-0 rounded-full transition-colors',
          checked ? 'bg-primary' : 'bg-muted-foreground/25',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform',
            checked && 'translate-x-4',
          )}
        />
      </span>
    </button>
  )
}
